import * as React from 'react';
import useViewer from 'hooks/useViewer';
import { useGetOptionFlowLazyQuery, useGetFreeOptionFlowLazyQuery } from 'types/generated/client';
import { AuthStatus } from 'constants/auth';
import { hasDashboardAccess } from 'services/client/token';
import { SHOWCASE_FLOW_LIMIT } from 'constants/user';
import OptionFlowTable from 'components/OptionFlowTable';
import { MobileLayout, SortDirection } from 'utils/shared/trades/types';
import { getDefaultFlowSideBasisSelection } from 'utils/shared/trades/presets';
import { filterTradesByFlowSideBasis } from 'utils/shared/trades/aggregateOptionFlow';
import { getFlowSideBasisLabel } from 'utils/shared/trades/flowSideBasis';
import { DEFAULT_FLOW_KEY } from 'components/OptionFlowTable/props';
import calculatePercentOtm from 'utils/shared/trades/calculatePercentOtm';
import getDerivedTradeValues from 'utils/shared/trades/getDerivedTradeValues';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { Timezone_Location_Enum } from 'types/generated/client';

interface OptionFlowTableShowcaseProps {
  /**
   * Optional prop to override the default showcase limit
   */
  limit?: number;
}

const OptionFlowTableShowcase: React.FC<OptionFlowTableShowcaseProps> = ({
  limit = SHOWCASE_FLOW_LIMIT,
}) => {
  const viewer = useViewer();
  const { user: currentUser } = useCurrentUser();
  const [getOptionFlowQuery, { data: paidData, loading: paidLoading }] =
    useGetOptionFlowLazyQuery();
  const [getFreeOptionFlowQuery, { data: freeData, loading: freeLoading }] =
    useGetFreeOptionFlowLazyQuery();

  const [flowSortKey, setFlowSortKey] = React.useState(DEFAULT_FLOW_KEY);
  const [flowSortDirection, setFlowSortDirection] = React.useState(SortDirection.None);
  const [isWatchListOnly, setIsWatchListOnly] = React.useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = React.useState(false);
  const [isEarningsOnly, setIsEarningsOnly] = React.useState(false);
  const flowSideBasis = React.useMemo(() => getDefaultFlowSideBasisSelection(), []);

  const isLoadingAuth = viewer.status === AuthStatus.Loading;
  const hasAccess = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);
  const isUserSession = viewer.status === AuthStatus.User;
  const timezoneLocation = currentUser?.timezoneLocation || Timezone_Location_Enum.Current;
  const isWallstreetTimezone = timezoneLocation === Timezone_Location_Enum.Wallstreet;

  // Fetch data once on mount
  React.useEffect(() => {
    if (isLoadingAuth) {
      return;
    }

    if (hasAccess) {
      getOptionFlowQuery();
    } else {
      getFreeOptionFlowQuery();
    }
  }, [isLoadingAuth, hasAccess, getOptionFlowQuery, getFreeOptionFlowQuery]);

  // Process and slice trades
  const trades = React.useMemo(() => {
    const rawTrades = hasAccess ? paidData?.option_flow : freeData?.free_option_flow;

    if (!rawTrades) {
      return [];
    }

    // Process trades similar to how it's done in the main flow page
    const processedTrades = rawTrades
      .map((t) => {
        const spot = t.spot || t.priceExternalApi;
        return {
          ...t,
          sector: t.tickerDetails?.sector || '',
          spot,
          otmPercent: calculatePercentOtm(t.strike, spot, t.cp || ''),
          // No blur in showcase mode - we're showing these intentionally
          isBlur: false,
        };
      })
      .map((trade) => {
        return getDerivedTradeValues(trade, isWallstreetTimezone);
      });

    return filterTradesByFlowSideBasis(processedTrades, flowSideBasis).slice(0, limit);
  }, [hasAccess, paidData, freeData, limit, isWallstreetTimezone, flowSideBasis]);

  const loading = paidLoading || freeLoading;

  // Constants for dynamic height calculation
  const TABLE_ROW_PIXEL_HEIGHT = 36.2;
  const HEADER_HEIGHT = 90; // Approximate header height
  const SHOWCASE_TABLE_CONTROLS_HEIGHT = 44;

  // Calculate dynamic height based on trade count
  const tableHeight =
    trades.length > 0 ? TABLE_ROW_PIXEL_HEIGHT * trades.length + HEADER_HEIGHT : 400;

  if (loading || isLoadingAuth) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="text-palette-black-7">Loading flow data...</div>
      </div>
    );
  }

  return (
    <div style={{ height: `${tableHeight + SHOWCASE_TABLE_CONTROLS_HEIGHT}px` }} className="w-full">
      <OptionFlowTable
        trades={trades}
        flowSortKey={flowSortKey}
        setFlowSortKey={setFlowSortKey}
        flowSortDirection={flowSortDirection}
        setFlowSortDirection={setFlowSortDirection}
        isWatchListOnly={isWatchListOnly}
        setIsWatchListOnly={setIsWatchListOnly}
        hasWatchListItems={false}
        isSoundEnabled={isSoundEnabled}
        setIsSoundEnabled={setIsSoundEnabled}
        watchListTickers={[]}
        hasAccess={hasAccess}
        isUserSession={isUserSession}
        isShowcase={true}
        isEarningsOnly={isEarningsOnly}
        setIsEarningsOnly={setIsEarningsOnly}
        defaultMobileLayout={MobileLayout.Table}
        flowSideBasis={flowSideBasis}
        flowSideLabel={getFlowSideBasisLabel(flowSideBasis)}
        putFlowBasis="premium"
      />
    </div>
  );
};

export default OptionFlowTableShowcase;
